import React from 'react'
import { Badge, Box, type StyleProps } from '@mui/material'
import { TFunction } from 'i18next'

import * as queries from './export-queries'

type ExportStatus =
  | 'completed'
  | 'failed'
  | 'completed_with_issues'
  | 'cancelled'
  | 'waiting_user_input'
  | 'pending'
  | 'in_progress'

type ExportHeaderProps = {
  job?: queries.ExportJobBasic
  showBadge?: boolean
  badgeColor?: 'error' | 'warning'
}

const getLabel = (jobBasic: queries.ExportJobBasic) => {
  if (jobBasic.exportType.includes('webhook/')) {
    return `${jobBasic.fileName.split('-')[0]}.${jobBasic.fileName.split('.')[1]}`
  }
  return `${jobBasic.exportType}.${jobBasic.fileName.split('.')[1]}`
}

const statusKeyMap = {
  completed: 'status.completed',
  failed: 'status.failed',
  completed_with_issues: 'status.completed_with_issues',
  cancelled: 'status.cancelled',
  waiting_user_input: 'status.waiting_user_input',
  pending: 'status.pending',
  in_progress: 'status.in_progress',
} as const satisfies Record<ExportStatus, string>

export const getStatusLabel = (status: ExportStatus, t: TFunction<'export'>): string =>
  t(statusKeyMap[status])

export const getProgress = (jobDetails?: queries.ExportJobDetails) => {
  if (!jobDetails) return 0
  return ((jobDetails.totalCompleted + jobDetails.totalFailed) / jobDetails.totalItems) * 100 || 0
}

export const commonExportStyles = {
  exportJobHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
  },
} satisfies StyleProps

export const ExportHeader = (props: ExportHeaderProps) => {
  const [tooltip, date] = React.useMemo(() => {
    if (!props.job?.createdAt) return ['', '']

    const language = navigator?.language || 'en-US'
    const dateInstance = new Date(props.job.createdAt)

    return [
      dateInstance.toLocaleDateString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
      dateInstance.toLocaleDateString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
    ]
  }, [props.job?.createdAt])

  if (!props.job) return null

  return (
    <Box flexGrow={1} display="flex" alignItems="center">
      <Badge
        variant="dot"
        color={props.badgeColor}
        invisible={!props.showBadge}
        sx={{ mr: 3, pr: 1 }}
        arial-label={`export-job-status-${props.job.status}`}
      >
        <Box>{getLabel(props.job)}</Box>
      </Badge>
      <Box title={tooltip}>{date}</Box>
    </Box>
  )
}
